// Context-line data builder — maps one match from MJ's club-matches
// contract (GET /api/clubs/<id>/matches/, PR #436: competitions schedule
// shape + club_side + competition{name, short_name}) onto ContextLine props.
//
// Suffix rules exactly as the handoff renders them (decorate()/ctx):
//   penalties  -> `(4–2 pens)`   (DE bundle: i.E.)
//   extra time -> `aet`          (DE: n.V., no parens)
//   aggregate  -> `(agg 3–6)`    (DE: ges.)
// Only when the data exists — otherwise no suffix at all (brief §2).

const teamOf = (match, side) =>
  match?.[side] || match?.teams?.[side] || null;

export const buildContextLine = (match) => {
  if (!match) return null;
  const home = teamOf(match, "home");
  const away = teamOf(match, "away");
  const score = match.score || {};
  const played =
    score.home != null && score.away != null && !match.tbd && !match.upcoming;

  // #436 ships penalties as score.penalty = {home, away}; keep the flat
  // penalty_home/penalty_away pair as a fallback for older shapes.
  const penHome = score.penalty?.home ?? score.penalty_home;
  const penAway = score.penalty?.away ?? score.penalty_away;

  let suffixText = null;
  if (played) {
    if (penHome != null && penAway != null) {
      suffixText = `(${penHome}–${penAway} pens)`;
    } else if (score.aet || score.ot) {
      suffixText = "aet";
    } else if (match.agg_score) {
      const agg = Array.isArray(match.agg_score)
        ? `${match.agg_score[0]}–${match.agg_score[1]}`
        : String(match.agg_score);
      suffixText = `(agg ${agg})`;
    }
  }

  const dateText = match.date_text || match.date || null;

  return {
    competitionName:
      match.competition?.name || match.competition?.short_name || null,
    // #436 ships round as {stage_id, group_num, round_num} — an object must
    // NEVER reach JSX (React #31 blanked the whole tab). Label if present,
    // else "Round {n}" (platform convention, competitionsUtils), else no chip.
    round:
      match.round?.label ||
      (typeof match.round === "string" ? match.round : null) ||
      (match.round?.round_num != null ? `Round ${match.round.round_num}` : null),
    homeName: home?.name || null,
    homeLogo: home?.logo || null,
    homeTeamId: home?.id || null,
    awayName: away?.name || null,
    awayLogo: away?.logo || null,
    awayTeamId: away?.id || null,
    scoreText: played ? `${score.home}–${score.away}` : null,
    suffixText,
    tbd: !!match.tbd,
    dateText,
    venue: match.venue || null,
    referee: match.referee || null,
  };
};
